import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import {
  Form,
  InputGroup,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";
import CustomPagination from "../../components/CustomPagination";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "../../components/DeleteModal";
import ViewBlogs from "../../components/blog/ViewBlogs";
import axiosInstance from "../../utils/axiosConfig";
import NoDateFound from "../../components/NoDateFound";
import Loader from "../../components/loader/Loader";
import { useGlobalState } from "../../context/GlobalStateContext";
import * as CustomToast from "../../components/CustomToastMessage";

function Category() {
  const [categories, setCategories] = useState([]);
  const {
    loading,
    setLoading,
    search,
    pagination,
    setPagination,
    handleSearch,
    handleLimitChange,
  } = useGlobalState();
  const navigate = useNavigate();

  const fetchCategories = useCallback(
    async (page = 1, limit = 10, searchQuery = "") => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/category/list`,
          {
            params: {
              page,
              limit,
              search: searchQuery,
            },
          }
        );
        if (response.data.success) {
          setCategories(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setCategories([]);
          setPagination((prev) => ({
            ...prev,
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
          }));
        }
      } catch (error) {
        console.error(
          error.response?.data?.message || "Error fetching categories"
        );
        setCategories([]);
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
        }));
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setPagination]
  );

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/category/delete/${id}`
      );
      if (response.data.success) {
        CustomToast.success(response.data.message);
        fetchCategories(pagination.currentPage, pagination.limit, search);
      }
    } catch (error) {
      if (error.response?.data?.status === 400) {
        CustomToast.error(error.response?.data?.message);
      }
      console.error(error.response?.data?.message || "Error deleting category");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(pagination.currentPage, pagination.limit, search);
  }, [pagination.currentPage, pagination.limit, search, fetchCategories]);

  const [trendingLoading, setTrendingLoading] = useState(false);

  const handleTrendingToggle = async (id, currentStatus) => {
    try {
      setTrendingLoading(true);
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/blog/trending/${id}`,
        {
          trending: !currentStatus,
        }
      );
      if (response.data.success) {
        // setBlogs(prevBlogs =>
        //     prevBlogs.map(blog =>
        //         blog._id === id ? { ...blog, trending: !currentStatus } : blog
        //     )
        // );
        CustomToast.success(response.data.message);
      }
    } catch (error) {
      if (error.response.data.status === 400) {
        CustomToast.error(error.response?.data?.message);
      }
      console.error(error.response?.data?.message || "Internal server error");
    } finally {
      setTrendingLoading(false);
    }
  };

  // Toggle active status handler
  const [activeLoading, setActiveLoading] = useState(false);
  const handleActiveToggle = async (id, currentStatus) => {
    try {
      setActiveLoading(true);
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/category/status/${id}`,
        {
          isActive: !currentStatus,
        }
      );
      if (response.data.success) {
        setCategories((prevCategories) =>
          prevCategories.map((cat) =>
            cat._id === id ? { ...cat, isActive: !currentStatus } : cat
          )
        );
        CustomToast.success(response.data.message);
      }
    } catch (error) {
      if (error.response?.data?.status === 400) {
        CustomToast.error(error.response?.data?.message);
      }
      console.error(error.response?.data?.message || "Error updating status");
    } finally {
      setActiveLoading(false);
    }
  };

  const handleBlurPriorityField = async ({ e, rowData }) => {
    const value = e?.target?.value;

    if (value === "" || value == rowData?.priority) {
      fetchCategories(pagination.currentPage, pagination.limit, search);
      return;
    }
    const formDataToSend = new FormData();
    formDataToSend.append("priority", value);

    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/category/edit/${rowData?._id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      CustomToast.success("Category updated successfully");
      fetchCategories(pagination.currentPage, pagination.limit, search);
    } catch (error) {
      if (error.response?.data?.status === 400) {
        CustomToast.error(error.response?.data?.message);
      }
      console.error(error.response?.data?.message || "Error updating category");
    } finally {
      // Any cleanup actions if necessary
    }
  };

  return (
    <Layout title="Category list">
      <main>
        <div className="card page-card">
          <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <p>Show</p>
              <Form.Select
                aria-label="Default select example"
                value={pagination.limit}
                onChange={handleLimitChange}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </Form.Select>
              <p>entries</p>
            </div>

            <div className="d-flex flex-column flex-md-row  align-items-start align-items-md-center gap-3 sm-w-100">
              <div className="header-search">
                <InputGroup>
                  <InputGroup.Text id="basic-addon1" className="bg-transparent">
                    <img src="/images/png/search.png" width={20} alt="" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    className="border-start-0 ps-0"
                    placeholder="search here..."
                    value={search}
                    onChange={handleSearch(fetchCategories)}
                  />
                </InputGroup>
              </div>
              <Link
                to="/category/add"
                className="btn-primary fs-16 fw-600 d-flex justify-content-center align-items-center gap-2 px-3 add-hover"
              >
                <img src="/images/png/plus.png" width={22} alt="" />
                Add Category
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="overflow-auto">
              <Table>
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th width="100" className="text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        <Loader />
                      </td>
                    </tr>
                  ) : categories.length > 0 ? (
                    categories.map((item, index) => (
                      <tr key={item._id}>
                        <td>
                          {(pagination.currentPage - 1) * pagination.limit +
                            index +
                            1}
                        </td>
                        <td>
                          <img
                            src={
                              item.categoryImage
                                ? `${process.env.REACT_APP_BACKEND_URL}/${item.categoryImage}`
                                : "https://img.freepik.com/premium-photo/adult-beautiful-woman-working-out-light-background_470178-19669.jpg"
                            }
                            width={119}
                            height={119}
                            className="rounded-3 object-fit-cover img-sm-w"
                            alt=""
                          />
                        </td>
                        <td>
                          <p className="mb-0 table-td-300">
                            {item.name ? item.name : "-"}
                          </p>
                        </td>
                        <td className="table-td-100">
                          <p className="mb-0 ">
                            {item.createdAt
                              ? new Date(item.createdAt)
                                  .toISOString()
                                  .split("T")[0]
                              : "-"}
                          </p>
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`active-switch-${item._id}`}
                            className="custom-switch"
                            checked={item.isActive}
                            onChange={() =>
                              handleActiveToggle(item._id, item.isActive)
                            }
                            disabled={loading || activeLoading}
                          />
                        </td>
                        <td>
                          <Form.Group controlId="priority">
                            <Form.Control
                              type="number"
                              name="priority"
                              defaultValue={item?.priority || ""}
                              placeholder="Priority"
                              className="text-center"
                              style={{ maxWidth: "80px" }}
                              min="1"
                              step="1"
                              onBlur={(e) =>
                                handleBlurPriorityField({ e, rowData: item })
                              }
                              onKeyDown={(e) => {
                                const { value } = e.target;
                                const invalidKeys = [
                                  "e",
                                  "E",
                                  "+",
                                  "-",
                                  ".",
                                  "ArrowDown",
                                  "ArrowUp",
                                ];
                                if (
                                  invalidKeys.includes(e.key) ||
                                  (e.key === "0" && !value)
                                ) {
                                  e.preventDefault();
                                  return;
                                }
                              }}
                              onPaste={(e) => {
                                if (isNaN(e.clipboardData.getData("text"))) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          </Form.Group>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <OverlayTrigger
                              key="top"
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-edit-${item._id}`}>
                                  Edit
                                </Tooltip>
                              }
                            >
                              <Link to={`/category/edit/${item._id}`}>
                                <img
                                  src="/images/png/edit.png"
                                  className="pointer"
                                  width={35}
                                  alt="edit"
                                />
                              </Link>
                            </OverlayTrigger>
                            <DeleteModal
                              onConfirm={() => handleDelete(item._id)}
                              loading={loading}
                              flag={"category"}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        <NoDateFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <CustomPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              limit={pagination.limit}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, currentPage: page }))
              }
            />
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default Category;
